/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, Input, OnInit, inject, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Subject, debounceTime, distinctUntilChanged, from, map, of, switchMap,
} from 'rxjs';
import { EMPTY_CONTENT_MARKDOWNS } from '../../constants';

type TopicField = 'category' | 'title' | 'content';

const FIELD_IDS: Record<TopicField, string> = {
  category: 'topicCategory',
  title: 'topicTitle',
  content: 'topicContent',
};

const SIMILAR_DEBOUNCE_MS = 400;

// /new/forums and /forums/:id/edit in the 2026 design: one form, with the category only on create. Errors show
// inline and in a summary (the AngularJS form only raised toasts), formatting-only markdown still counts as empty,
// and the old "check for an existing topic" banner becomes live similar topics under the title.
@Component({
  selector: 'ut-topic-form-page',
  templateUrl: './topic-form-page.component.html',
  styleUrls: ['./topic-form-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TopicFormPageComponent implements OnInit {
  // The `editDiscussion` state's topic; unset when creating.
  @Input() discussion: any;

  readonly fieldIds = FIELD_IDS;

  categories = signal<any[]>([]);
  categoryId = signal<number | null>(null);
  // The category page the form was opened from, for the back link; unlike categoryId it ignores later changes to the select.
  fromCategory = signal<{ id: number; name: string } | null>(null);
  showReminder = signal(true);
  title = signal('');
  content = signal('');
  submitted = signal(false);
  saving = signal(false);
  serverError = signal('');
  similar = signal<any[]>([]);

  private titleChanges = new Subject<string>();
  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject('DiscussionService') private DiscussionService: any,
    @Inject('DiscussionCategoryService') private DiscussionCategoryService: any,
    @Inject('SearchService') private SearchService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('ProgressBarService') private ProgressBarService: any,
    @Inject('ServerMessages') private ServerMessages: any,
    @Inject('$state') private $state: any,
    private el: ElementRef<HTMLElement>,
  ) {}

  get isEdit(): boolean {
    return !!this.discussion;
  }

  // Focus goes to the heading, so it isn't lost with the button.
  dismissReminder(): void {
    this.showReminder.set(false);
    this.el.nativeElement.querySelector<HTMLElement>('.page-h__title')?.focus();
  }

  ngOnInit(): void {
    if (this.isEdit) {
      this.title.set(this.discussion.title || '');
      this.content.set(this.discussion.content || '');
      return;
    }
    this.DiscussionCategoryService.allForSelect().then((categories) => {
      this.categories.set(categories);
      // From a category page's "Create a new topic"; ignored if it isn't one of the choices.
      const fromPage = Number(this.$state.params?.categoryId);
      const category = categories.find((c) => c.id === fromPage);
      if (category) {
        this.categoryId.set(fromPage);
        this.fromCategory.set({ id: category.id, name: category.name });
      } else if (fromPage) {
        // A top-level category isn't a choice in the select, but the back link still belongs to its page.
        this.DiscussionCategoryService.find(fromPage).then((res) => {
          if (res?.data?.name) this.fromCategory.set({ id: fromPage, name: res.data.name });
        }, () => {});
      }
    });

    // switchMap drops the answer to a query that's been superseded.
    const searchTopics = this.SearchService.searchOneFactory('Topic');
    this.titleChanges.pipe(
      map((title) => title.trim()),
      debounceTime(SIMILAR_DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap((query) => (query.length < 3
        ? of([])
        : from(searchTopics({ query, per_page: 3 }).then((res) => res.data, () => [])))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((topics: any[]) => this.similar.set(topics));
  }

  setTitle(value: string): void {
    this.title.set(value);
    if (!this.isEdit) this.titleChanges.next(value);
  }

  error(field: TopicField): string {
    if (!this.submitted()) return '';
    switch (field) {
      case 'category':
        return !this.isEdit && !this.categoryId() ? 'Choose a category.' : '';
      case 'title':
        return this.title().trim() ? '' : 'Add a title.';
      default: {
        const text = this.content().trim();
        return !text || EMPTY_CONTENT_MARKDOWNS.includes(text) ? 'Add your topic text. Formatting on its own doesn\'t count.' : '';
      }
    }
  }

  get errors(): { field: TopicField; message: string }[] {
    return (Object.keys(FIELD_IDS) as TopicField[])
      .map((field) => ({ field, message: this.error(field) }))
      .filter((e) => e.message);
  }

  focusField(field: TopicField): void {
    // The editor's own textarea carries the id passed to ut-editor.
    this.el.nativeElement.querySelector<HTMLElement>(`#${FIELD_IDS[field]}`)?.focus();
  }

  submit(): void {
    if (this.saving()) return;
    this.submitted.set(true);
    this.serverError.set('');
    if (this.errors.length) {
      setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.errsum')?.focus());
      return;
    }
    if (!this.isEdit && this.LightTesterErrorService.show()) return;

    this.saving.set(true);
    this.ProgressBarService.start();
    const request = this.isEdit
      ? this.DiscussionService.update({ id: this.discussion.id, title: this.title(), content: this.content() })
      : this.DiscussionService.create({ title: this.title(), category_id: this.categoryId(), content: this.content() });
    request.then((res) => {
      this.AlertService.add(this.isEdit ? 'Forum topic successfully saved' : 'Forum topic successfully created');
      this.$state.go('singlediscussionpage', { page: 1, discussionId: res.data.id });
    }, (response) => {
      this.serverError.set(this.ServerMessages.prettyMessage(response));
      this.saving.set(false);
      setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.errsum')?.focus());
    }).finally(() => this.ProgressBarService.done());
  }
}
